// La regola di assegnazione dei Lead nuovi (piano M6, Task 13, D16; mock-up `regole.html?sezione=assegnazione`): a chi va un Lead
// che entra senza un owner scelto. Tre modi: a turno fra le persone scelte (`round_robin`), tutti a una persona (`single`),
// a nessuno (`manual`: lo assegna a mano chi guida il team). Il turno è deterministico: il prossimo è il primo idoneo dopo
// l'ultimo servito, nell'ordine in cui la regola è salvata, e dall'ultimo della lista si torna al primo.
//
// Il dominio non conosce il database: riceve gli identificativi di chi è idoneo adesso (un'appartenenza attiva e assegnabile,
// che la proiezione operativa dice) e l'ultimo servito (il puntatore del turno, `assignment_pointer`). Chi non è idoneo salta
// il turno ma resta nella regola, così rientra da solo quando torna attivo. Il turno può essere pesato (ASSEG-2, Lucio
// 30/10/2026: «2 a quello e 1 a quell'altro»): ogni persona riceve di fila tanti Lead quanto il suo peso, da 1 a
// `MAX_ASSIGNMENT_WEIGHT` (1 se non è scritto), poi tocca al successivo; il puntatore ricorda anche quanti Lead di fila ha
// preso l'ultimo servito. Niente limiti per campagna. Il file non importa nulla, così il browser lo porta nell'anteprima.
/** I tre modi del mock-up: «A turno fra le persone scelte», «Tutti a una persona», «A nessuno». */
export const ASSIGNMENT_MODES = ["round_robin", "single", "manual"];
/** «Un Lead che torna»: «Resta al suo owner…» (`keep_owner`) oppure «Si assegna come un Lead nuovo» (`as_new`). */
export const RETURNING_RULES = ["keep_owner", "as_new"];
/** Il peso più alto di una persona del turno: quanti Lead nuovi riceve di fila prima che tocchi al successivo. */
export const MAX_ASSIGNMENT_WEIGHT = 10;
/** Il peso di una persona nella regola: un intero fra 1 e `MAX_ASSIGNMENT_WEIGHT`; 1 se non è scritto o non è valido. */
export function weightOf(rule, membershipId) {
    const weight = rule.weights?.[membershipId];
    return typeof weight === "number" && Number.isInteger(weight) && weight >= 1 && weight <= MAX_ASSIGNMENT_WEIGHT ? weight : 1;
}
/**
 * Quanti Lead di fila avrà preso `assignee` dopo il prossimo: uno in più se è di nuovo l'ultimo servito, altrimenti 1. Non va oltre
 * `MAX_ASSIGNMENT_WEIGHT`: nessun peso è più alto, e con un solo idoneo il numero non cresce senza fine.
 */
export function servedInRowAfter(assignee, lastServed, servedInRow) {
    return assignee === lastServed ? Math.min(servedInRow + 1, MAX_ASSIGNMENT_WEIGHT) : 1;
}
/** Nessuna assegnazione da sola finché il Super admin non sceglie: oggi è così. */
export const DEFAULT_ASSIGNMENT_RULE = { mode: "manual", people: [], single: null, returning: "keep_owner" };
/** I prossimi Lead nuovi che l'anteprima mostra: «I prossimi 6 Lead nuovi». */
export const ASSIGNMENT_PREVIEW_COUNT = 6;
/**
 * A chi va il prossimo Lead nuovo, oppure `null` se non va a nessuno: il modo è `manual`, nessuno del turno è idoneo, oppure
 * la persona unica non è idonea (non passa a chi è nel turno). A turno è il primo idoneo dopo `lastServed` nell'ordine di
 * `rule.people`, con il giro che riparte dal primo; se `lastServed` non è nel turno (mai servito, oppure tolto dalla regola) si
 * parte dal primo. Se l'unico idoneo è lo stesso `lastServed`, tocca ancora a lui. Col turno pesato `lastServed` tiene il turno
 * finché i Lead presi di fila (`servedInRow`, 1 se non si sa) sono meno del suo peso, se è ancora nel turno e idoneo.
 */
export function nextAssignee(rule, lastServed, eligible, servedInRow = 1) {
    if (rule.mode === "manual")
        return null;
    if (rule.mode === "single")
        return rule.single !== null && eligible.has(rule.single) ? rule.single : null;
    const { people } = rule;
    const last = lastServed === null ? -1 : people.indexOf(lastServed);
    if (lastServed !== null && last >= 0 && eligible.has(lastServed) && servedInRow < weightOf(rule, lastServed))
        return lastServed;
    for (let step = 1; step <= people.length; step += 1) {
        const candidate = people[(last + step) % people.length];
        if (candidate !== undefined && eligible.has(candidate))
            return candidate;
    }
    return null;
}
/**
 * I prossimi `count` Lead nuovi a chi andrebbero, uno dopo l'altro, partendo da `lastServed`: «I prossimi 6 Lead nuovi: …».
 * Vuoto se non va a nessuno. Non cambia niente: è lo stesso calcolo di `nextAssignee` ripetuto.
 */
export function previewNext(rule, lastServed, eligible, count = ASSIGNMENT_PREVIEW_COUNT, servedInRow = 1) {
    const next = [];
    let served = lastServed;
    let inRow = servedInRow;
    while (next.length < count) {
        const assignee = nextAssignee(rule, served, eligible, inRow);
        if (assignee === null)
            break;
        next.push(assignee);
        inRow = servedInRowAfter(assignee, served, inRow);
        served = assignee;
    }
    return next;
}
//# sourceMappingURL=assignment.js.map